export const SceneAssetKind = {
  CHARACTER: 'character',
  OBJECT: 'object',
  // Temporary symbol alias for existing call sites during the object migration.
  PROP: 'object',
  ENVIRONMENT: 'environment',
  BRAND: 'brand',
  STYLE: 'style',
};

export const SCENE_ASSET_KINDS = Object.freeze([
  SceneAssetKind.CHARACTER,
  SceneAssetKind.OBJECT,
  SceneAssetKind.ENVIRONMENT,
  SceneAssetKind.BRAND,
  SceneAssetKind.STYLE,
]);

/**
 * Reference-bucket vocabulary. Every scene-asset kind may carry reference pixels; the
 * only translation between the type system and the bucket layer is that `object` is
 * spelled `prop` in bucket space. Prompt-image ROLES (`structure`, `source`, ...) are
 * not kinds and never resolve here: `roleBuckets.js` owns role space.
 */
const REFERENCE_BUCKET_ALIASES = Object.freeze({
  [SceneAssetKind.OBJECT]: 'prop',
});

export const SCENE_ASSET_REFERENCE_BUCKET_KINDS = Object.freeze(
  SCENE_ASSET_KINDS.map((kind) => REFERENCE_BUCKET_ALIASES[kind] || kind),
);

const REFERENCE_BUCKET_KIND_SET = new Set(SCENE_ASSET_REFERENCE_BUCKET_KINDS);

/**
 * Kind-space (`object`) or bucket-space (`prop`) in, bucket-space out; `null` for
 * anything that is not a scene-asset kind. Total over both vocabularies so no caller
 * has to know which one it is holding.
 */
export function getSceneAssetReferenceBucketKind(kind) {
  const bucketKind = REFERENCE_BUCKET_ALIASES[kind] || kind;
  return REFERENCE_BUCKET_KIND_SET.has(bucketKind) ? bucketKind : null;
}

export const SceneAssetStyleFamily = {
  VISUAL: 'visual',
  MOTION: 'motion',
  COMPOSITION: 'composition',
  VOICE: 'voice',
};

export const SCENE_ASSET_STYLE_FAMILIES = Object.freeze(Object.values(SceneAssetStyleFamily));

export const SceneAssetBrandRole = {
  LOGO: 'logo',
  WORDMARK: 'wordmark',
  LOCKUP: 'lockup',
  TYPOGRAPHY: 'typography',
  COLOR: 'color',
  GRAPHIC_MARK: 'graphicMark',
  PATTERN: 'pattern',
  PACKAGING_MARK: 'packagingMark',
  LEGAL_MARK: 'legalMark',
};

export const SCENE_ASSET_BRAND_ROLES = Object.freeze(Object.values(SceneAssetBrandRole));

export function isSceneAssetKind(value) {
  return SCENE_ASSET_KINDS.includes(value);
}

export function normalizeSceneAssetKind(value) {
  if (value === 'prop') return SceneAssetKind.OBJECT;
  return isSceneAssetKind(value) ? value : SceneAssetKind.CHARACTER;
}

export function normalizeSceneAssetStyleFamily(value) {
  return SCENE_ASSET_STYLE_FAMILIES.includes(value) ? value : SceneAssetStyleFamily.VISUAL;
}

export function normalizeSceneAssetBrandRole(value) {
  return SCENE_ASSET_BRAND_ROLES.includes(value) ? value : SceneAssetBrandRole.LOGO;
}
